import React, { useState } from 'react';

const emptyCreateForm = {
    title: '',
    slug: '',
    description: '',
    status: 'draft',
    approvalRequired: true,
    membersOnly: false,
    allowRegistrationViaLink: true,
    capacity: 50,
    waitlistEnabled: true,
    registrationOpensAt: '',
    registrationClosesAt: '',
    startsAt: '',
    endsAt: '',
    venueName: '',
    venueAddress: '',
    requireStyle: true,
    requireWeight: true
};

function makeSlug(value) {
    return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function normalizeFieldErrors(details) {
    if (!details || typeof details !== 'object') return {};

    const mapped = {};

    for (const [key, messages] of Object.entries(details)) {
        const firstMessage = Array.isArray(messages) ? messages[0] : String(messages);

        switch (key) {
            case 'slug':
                mapped.slug = firstMessage.includes('pattern')
                    ? 'Use only lowercase letters, numbers, and hyphens.'
                    : firstMessage;
                break;
            case 'title':
                mapped.title = firstMessage;
                break;
            case 'description':
                mapped.description = firstMessage;
                break;
            case 'capacity':
                mapped.capacity = firstMessage;
                break;
            case 'registrationOpensAt':
                mapped.registrationOpensAt = firstMessage;
                break;
            case 'registrationClosesAt':
                mapped.registrationClosesAt = firstMessage;
                break;
            case 'startsAt':
                mapped.startsAt = firstMessage;
                break;
            case 'endsAt':
                mapped.endsAt = firstMessage;
                break;
            case 'venue.name':
                mapped.venueName = firstMessage;
                break;
            case 'venue.address':
                mapped.venueAddress = firstMessage;
                break;
            case 'status':
                mapped.status = firstMessage;
                break;
            default:
                mapped[key] = firstMessage;
                break;
        }
    }

    return mapped;
}

function EventCreateForm({ saving, onCreateEvent }) {
    const [createForm, setCreateForm] = useState(emptyCreateForm);
    const [fieldErrors, setFieldErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [slugTouched, setSlugTouched] = useState(false);

    function updateCreateField(key, value) {
        setCreateForm((prev) => {
            const next = { ...prev, [key]: value };

            if (key === 'title' && !slugTouched) {
                next.slug = makeSlug(value);
            }

            return next;
        });

        setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
        setFormError('');
    }

    function updateSlug(value) {
        setSlugTouched(true);
        setCreateForm((prev) => ({
            ...prev,
            slug: makeSlug(value)
        }));
        setFieldErrors((prev) => ({ ...prev, slug: undefined }));
        setFormError('');
    }

    function renderFieldError(key) {
        if (!fieldErrors[key]) return null;

        return (
            <div style={{ color: '#ff6b6b', fontSize: '0.85rem', textAlign: 'left', marginTop: '-0.35rem' }}>
                {fieldErrors[key]}
            </div>
        );
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setFieldErrors({});
        setFormError('');

        try {
            const finalSlug = makeSlug(createForm.slug || createForm.title);

            const payload = {
                title: createForm.title.trim(),
                description: createForm.description.trim(),
                status: createForm.status,
                approvalRequired: !!createForm.approvalRequired,
                membersOnly: !!createForm.membersOnly,
                allowRegistrationViaLink: !!createForm.allowRegistrationViaLink,
                capacity: Number(createForm.capacity),
                waitlistEnabled: !!createForm.waitlistEnabled,
                registrationOpensAt: new Date(createForm.registrationOpensAt).toISOString(),
                registrationClosesAt: new Date(createForm.registrationClosesAt).toISOString(),
                startsAt: new Date(createForm.startsAt).toISOString(),
                endsAt: new Date(createForm.endsAt).toISOString(),
                venue: {
                    name: createForm.venueName.trim(),
                    address: createForm.venueAddress.trim()
                },
                registrationForm: {
                    requireStyle: !!createForm.requireStyle,
                    requireWeight: !!createForm.requireWeight,
                    customQuestions: []
                }
            };

            if (finalSlug) {
                payload.slug = finalSlug;
            }

            await onCreateEvent(payload);

            setCreateForm(emptyCreateForm);
            setFieldErrors({});
            setFormError('');
            setSlugTouched(false);
        } catch (err) {
            const details = err?.payload?.details;
            const mappedErrors = normalizeFieldErrors(details);

            if (Object.keys(mappedErrors).length > 0) {
                setFieldErrors(mappedErrors);
                setFormError('Please correct the highlighted fields.');
            } else {
                setFormError(err.message || 'Failed to create event.');
            }
        }
    }

    return (
        <form
            onSubmit={handleSubmit}
            style={{
                display: 'grid',
                gap: '0.7rem',
                marginBottom: '1.5rem',
                textAlign: 'left'
            }}
        >
            {formError && (
                <div style={{ color: '#ff6b6b', marginBottom: '0.3rem', textAlign: 'left' }}>
                    {formError}
                </div>
            )}

            <input
                type="text"
                placeholder="Event title"
                value={createForm.title}
                onChange={(e) => updateCreateField('title', e.target.value)}
                required
            />
            {renderFieldError('title')}

            <input
                type="text"
                placeholder="Slug (auto-generated from title)"
                value={createForm.slug}
                onChange={(e) => updateSlug(e.target.value)}
            />
            <div style={{ color: '#888', fontSize: '0.82rem', textAlign: 'left', marginTop: '-0.3rem' }}>
                Public event link id. Auto-generated from title. Example: spring-kumite-2026
            </div>
            {renderFieldError('slug')}

            <textarea
                placeholder="Description"
                value={createForm.description}
                onChange={(e) => updateCreateField('description', e.target.value)}
                rows={4}
                style={{ background: '#111', color: '#fff', border: '1px solid #333', padding: '0.8rem' }}
            />
            {renderFieldError('description')}

            <select
                value={createForm.status}
                onChange={(e) => updateCreateField('status', e.target.value)}
                style={{ background: '#111', color: '#fff', border: '1px solid #333', padding: '0.8rem' }}
            >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="closed">Closed</option>
            </select>
            {renderFieldError('status')}

            <input
                type="number"
                placeholder="Capacity"
                value={createForm.capacity}
                onChange={(e) => updateCreateField('capacity', e.target.value)}
                required
            />
            {renderFieldError('capacity')}

            <input
                type="text"
                placeholder="Venue name"
                value={createForm.venueName}
                onChange={(e) => updateCreateField('venueName', e.target.value)}
                required
            />
            {renderFieldError('venueName')}

            <input
                type="text"
                placeholder="Venue address"
                value={createForm.venueAddress}
                onChange={(e) => updateCreateField('venueAddress', e.target.value)}
                required
            />
            {renderFieldError('venueAddress')}

            <label>
                Registration opens
                <input
                    type="datetime-local"
                    value={createForm.registrationOpensAt}
                    onChange={(e) => updateCreateField('registrationOpensAt', e.target.value)}
                    required
                />
            </label>
            {renderFieldError('registrationOpensAt')}

            <label>
                Registration closes
                <input
                    type="datetime-local"
                    value={createForm.registrationClosesAt}
                    onChange={(e) => updateCreateField('registrationClosesAt', e.target.value)}
                    required
                />
            </label>
            {renderFieldError('registrationClosesAt')}

            <label>
                Event starts
                <input
                    type="datetime-local"
                    value={createForm.startsAt}
                    onChange={(e) => updateCreateField('startsAt', e.target.value)}
                    required
                />
            </label>
            {renderFieldError('startsAt')}

            <label>
                Event ends
                <input
                    type="datetime-local"
                    value={createForm.endsAt}
                    onChange={(e) => updateCreateField('endsAt', e.target.value)}
                    required
                />
            </label>
            {renderFieldError('endsAt')}

            <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    checked={createForm.approvalRequired}
                    onChange={(e) => updateCreateField('approvalRequired', e.target.checked)}
                />
                Approval required
            </label>

            <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    checked={createForm.membersOnly}
                    onChange={(e) => updateCreateField('membersOnly', e.target.checked)}
                />
                Members only
            </label>

            <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    checked={createForm.allowRegistrationViaLink}
                    onChange={(e) => updateCreateField('allowRegistrationViaLink', e.target.checked)}
                />
                Allow registration via link
            </label>

            <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    checked={createForm.waitlistEnabled}
                    onChange={(e) => updateCreateField('waitlistEnabled', e.target.checked)}
                />
                Waitlist enabled
            </label>

            <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    checked={createForm.requireStyle}
                    onChange={(e) => updateCreateField('requireStyle', e.target.checked)}
                />
                Require style
            </label>

            <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    checked={createForm.requireWeight}
                    onChange={(e) => updateCreateField('requireWeight', e.target.checked)}
                />
                Require weight
            </label>

            <button type="submit" disabled={saving}>
                {saving ? 'SAVING...' : 'Create Event'}
            </button>
        </form>
    );
}

export default EventCreateForm;